import type { FieldType } from '@/types/evaluation';
import type { EnumResponseType } from '@/api';

export type FieldCategory =
  | 'boolean'
  | 'number'
  | 'datetime'
  | 'currency'
  | 'enum-single'
  | 'enum-multiple'
  | 'list'
  | 'entity'
  | 'calendar-event'
  | 'other';

const isNumberType = (t: FieldType): boolean => t === 'int' || t === 'number' || t === 'float';

const isListType = (t: FieldType): boolean => typeof t === 'string' && t.startsWith('list[');

const isCurrencyType = (t: FieldType): boolean =>
  t === 'CurrencyAmount' ||
  (typeof t === 'object' && (t as Record<string, unknown>).type === 'CurrencyAmount');

const isEnumType = (t: FieldType): t is EnumResponseType => typeof t === 'object' && 'values' in t;

const isEntityType = (t: FieldType): boolean => t === 'Entity' || t === 'list[Entity]';

const isCalendarEventType = (t: FieldType): boolean => t === 'CalendarEvents';

/**
 * Map any FieldType to a simple FieldCategory.
 */
export function getFieldCategory(t: FieldType | undefined): FieldCategory {
  if (!t) return 'other';
  if (t === 'bool') return 'boolean';
  if (isNumberType(t)) return 'number';
  if (t === 'date') return 'datetime';
  if (t === 'datetime') return 'datetime';
  if (isCurrencyType(t)) return 'currency';
  if (isEnumType(t)) {
    return t.multiple_choices ? 'enum-multiple' : 'enum-single';
  }
  if (isListType(t)) return 'list';
  if (isEntityType(t)) return 'entity';
  if (isCalendarEventType(t)) return 'calendar-event';
  return 'other';
}

/**
 * Extract the inner type from a list type (e.g., 'list[str]' -> 'str')
 */
export function getListItemType(fieldType: FieldType): FieldType {
  if (!isListType(fieldType)) return 'str';
  const typeStr = fieldType as string;
  return typeStr.substring(5, typeStr.length - 1) as FieldType;
}
